import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import type { AppReleaseDto } from '@familynest/shared'
import { useAuth } from './store/auth'
import { SetupScreen } from './components/SetupScreen'
import { LoginScreen } from './components/LoginScreen'
import { AppShell } from './components/AppShell'
import { UpdateDialog } from './components/UpdateDialog'
import { checkForUpdate } from './lib/appUpdate'
import { takeOidcResult } from './lib/oidc'

/**
 * App-Einstieg: prüft die Session und zeigt Login oder die App-Shell.
 */
export default function App(): React.JSX.Element {
  const { status, init } = useAuth()
  const [update, setUpdate] = useState<AppReleaseDto | null>(null)
  const [updateDismissed, setUpdateDismissed] = useState(false)
  // Rückmeldung des Authentik-Flows (ADR-082). Bei Erfolg landet der Nutzer auf „/",
  // also hier; der Fehlerfall am Login wird in LoginScreen gezeigt. `takeOidcResult`
  // räumt die Query danach aus der Adresszeile.
  const [ssoNote, setSsoNote] = useState<string | null>(null)

  useEffect(() => {
    void init()
  }, [init])

  useEffect(() => {
    if (status !== 'authed') return
    const r = takeOidcResult()
    if (r.connected) {
      setSsoNote('Authentik-Konto verbunden.')
      const t = setTimeout(() => setSsoNote(null), 5000)
      return () => clearTimeout(t)
    }
    if (r.error) setSsoNote(r.error)
    return undefined
  }, [status])

  // Nach dem Login (nur nativ) auf eine neue App-Version prüfen (ADR-051).
  useEffect(() => {
    if (status === 'authed') void checkForUpdate().then(setUpdate)
  }, [status])

  if (status === 'loading') {
    return (
      <div className="flex min-h-full items-center justify-center">
        <Loader2 className="size-6 animate-spin" style={{ color: 'var(--color-muted)' }} />
      </div>
    )
  }

  if (status === 'setup') return <SetupScreen />
  if (status !== 'authed') return <LoginScreen />
  return (
    <>
      <AppShell />
      {ssoNote && (
        <div
          role="status"
          className="fixed inset-x-0 top-0 z-[60] px-4 py-2 text-center text-sm"
          style={{ backgroundColor: 'var(--color-card)', color: 'var(--color-ink)', paddingTop: 'calc(0.5rem + env(safe-area-inset-top))' }}
          onClick={() => setSsoNote(null)}
        >
          {ssoNote}
        </div>
      )}
      {update && !updateDismissed && <UpdateDialog release={update} onClose={() => setUpdateDismissed(true)} />}
    </>
  )
}
